import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  useAvailableBulkBatches,
  useRepackers,
  useStartRepacking,
} from '@hooks/useRepacking'

const repackingSchema = z.object({
  bulkBatchId: z.coerce.number().positive('Bulk batch is required'),
  inputQty: z.coerce.number().positive('Input quantity must be greater than 0'),
  repackerUserId: z.coerce.number().positive('Repacker is required'),
})

type RepackingFormData = z.infer<typeof repackingSchema>

export function RepackingFormPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null)

  const { data: repackersData } = useRepackers()
  const { data: batchesData } = useAvailableBulkBatches()
  const startRepacking = useStartRepacking()

  const repackers = repackersData?.data?.items || []
  const batches = batchesData?.data || []
  const selectedBatch = selectedBatchId
    ? batches.find((b) => b.id === selectedBatchId)
    : null

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RepackingFormData>({
    defaultValues: {
      bulkBatchId: 0,
      inputQty: 0,
      repackerUserId: 0,
    },
  })

  const inputQty = watch('inputQty')

  const expectedLoss = selectedBatch
    ? (inputQty * (selectedBatch.product.expectedLossPct || 0)) / 100
    : 0
  const expectedYield = inputQty - expectedLoss

  const onSubmit = async (data: RepackingFormData) => {
    try {
      setError(null)
      const result = await startRepacking.mutateAsync(data)
      navigate(`/dashboard/repacking/${result.data.id}`)
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Failed to start repacking job'
      )
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Start New Repacking Job
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Select a bulk batch and assign repacker
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/repacking')}
          className="btn-secondary"
        >
          ← Back
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Select Bulk Batch</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Available Bulk Batch *
              </label>
              <select
                {...register('bulkBatchId')}
                onChange={(e) => setSelectedBatchId(Number(e.target.value) || null)}
                className="w-full"
              >
                <option value="">Select Bulk Batch</option>
                {batches.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.batchNo} - {batch.product.name} ({batch.availableQtyForRepack.toFixed(2)} {batch.product.baseUom})
                  </option>
                ))}
              </select>
              {errors.bulkBatchId && (
                <span className="text-red-500 text-sm">
                  {errors.bulkBatchId.message}
                </span>
              )}
            </div>

            {selectedBatch && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Product</p>
                    <p className="font-medium">
                      {selectedBatch.product.name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Received Date</p>
                    <p className="font-medium">
                      {new Date(selectedBatch.receivedDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Available Quantity</p>
                    <p className="font-medium">
                      {selectedBatch.availableQtyForRepack.toFixed(2)} {selectedBatch.product.baseUom}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Expected Loss %</p>
                    <p className="font-medium">
                      {selectedBatch.product.expectedLossPct || 0}%
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Repacking Details</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Input Quantity *
              </label>
              <input
                {...register('inputQty', { valueAsNumber: true })}
                type="number"
                min="0"
                step="0.1"
                className="w-full"
                disabled={!selectedBatch}
              />
              {errors.inputQty && (
                <span className="text-red-500 text-sm">
                  {errors.inputQty.message}
                </span>
              )}
              {selectedBatch && inputQty > selectedBatch.availableQtyForRepack && (
                <span className="text-red-500 text-xs mt-1 block">
                  Exceeds available quantity
                </span>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Repacker *
              </label>
              <select {...register('repackerUserId')} className="w-full">
                <option value="">Select Repacker</option>
                {repackers.map((repacker) => (
                  <option key={repacker.id} value={repacker.id}>
                    {repacker.fullName}
                  </option>
                ))}
              </select>
              {errors.repackerUserId && (
                <span className="text-red-500 text-sm">
                  {errors.repackerUserId.message}
                </span>
              )}
            </div>
          </div>
        </div>

        {inputQty > 0 && selectedBatch && (
          <div className="card bg-gray-50">
            <div className="card-header">
              <h2 className="card-title">Yield Calculation</h2>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded border border-gray-200">
                <p className="text-xs text-gray-600 uppercase font-semibold">
                  Input Quantity
                </p>
                <p className="text-2xl font-bold mt-1">
                  {inputQty.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedBatch.product.baseUom}
                </p>
              </div>

              <div className="bg-white p-4 rounded border border-gray-200">
                <p className="text-xs text-gray-600 uppercase font-semibold">
                  Expected Loss
                </p>
                <p className="text-2xl font-bold mt-1 text-orange-600">
                  {expectedLoss.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {(expectedLoss / inputQty * 100).toFixed(1)}%
                </p>
              </div>

              <div className="bg-white p-4 rounded border border-gray-200">
                <p className="text-xs text-gray-600 uppercase font-semibold">
                  Expected Yield
                </p>
                <p className="text-2xl font-bold mt-1 text-green-600">
                  {expectedYield.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedBatch.product.baseUom}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end pt-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard/repacking')}
            className="btn-secondary"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary disabled:opacity-50"
            disabled={isSubmitting || !selectedBatch}
          >
            {isSubmitting ? 'Starting...' : 'Start Repacking Job'}
          </button>
        </div>
      </form>
    </div>
  )
}
